/**
 * Main Application Entry Point
 * Initializes all modules and handles application lifecycle
 */

import { Navbar } from './modules/navbar.js';
import { CTAButtons } from './modules/cta.js';
import { FAQ } from './modules/faq.js';
import { ScrollAnimations } from './modules/animations.js';
import { Typewriter } from './modules/typewriter.js';
import { CONFIG } from './config/constants.js';

class App {
    constructor() {
        this.modules = {};
        this.init();
    }

    /**
     * Initialize application
     */
    init() {
        // Wait for DOM to be ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.onReady());
        } else {
            this.onReady();
        }
    }

    /**
     * Called when DOM is ready
     */
    onReady() {
        console.log(`${CONFIG.EXTENSION_NAME} Website v${CONFIG.VERSION}`);

        // Initialize modules
        this.initModules();

        // Setup global event listeners
        this.setupGlobalListeners();
    }

    /**
     * Initialize all application modules
     */
    initModules() {
        try {
            this.modules.navbar = new Navbar();
            this.modules.ctaButtons = new CTAButtons();
            this.modules.faq = new FAQ();
            this.modules.animations = new ScrollAnimations();
            this.modules.typewriter = new Typewriter();

            console.log('All modules initialized successfully');
        } catch (error) {
            console.error('Error initializing modules:', error);
        }
    }

    /**
     * Setup global event listeners
     */
    setupGlobalListeners() {
        // Handle window resize
        window.addEventListener('resize', this.debounce(() => {
            this.handleResize();
        }, 250));

        // Handle visibility change
        document.addEventListener('visibilitychange', () => {
            this.handleVisibilityChange();
        });
    }

    /**
     * Handle window resize
     */
    handleResize() {
        // Add resize handling logic here
        console.log('Window resized');
    }

    /**
     * Handle page visibility change
     */
    handleVisibilityChange() {
        if (document.hidden) {
            console.log('Page hidden');
        } else {
            console.log('Page visible');
        }
    }

    /**
     * Debounce utility function
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function}
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Cleanup and destroy
     */
    destroy() {
        // Cleanup modules
        Object.values(this.modules).forEach(module => {
            if (module.destroy && typeof module.destroy === 'function') {
                module.destroy();
            }
        });

        console.log('Application destroyed');
    }
}

// Initialize application
const app = new App();

// Export for potential external access
window.zenGPTApp = app;

export default app;
